import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils.js';
import type {
  FeatureGeometry,
  PlaceCandidate,
  SpatialLayerObject,
} from './GlobalMapSearch.types';

/**
 * Resolve a single (lat, lng) centroid for a spatial object so the map can
 * pan to it / measure distance from the user.
 *
 * Resolution order, mirroring how the rest of the app (StaticLayerCard,
 * EllingtonCard helpers, GeoTiffMapCard) reads coordinates from a spatial
 * object:
 *   1. `spatialObject.center.coordinates` — canonical [lng, lat] centroid
 *   2. `spatialObject.shape.features[0].geometry.coordinates` — Point shape
 *   3. First vertex of a Polygon shape, when present
 */
export const getSpatialObjectCenter = (
  obj: SpatialLayerObject | undefined,
): { latitude: number; longitude: number } | null => {
  if (!obj) return null;

  const centerCoords = obj.center?.coordinates;
  if (
    Array.isArray(centerCoords) &&
    typeof centerCoords[0] === 'number' &&
    typeof centerCoords[1] === 'number'
  ) {
    return { latitude: centerCoords[1], longitude: centerCoords[0] };
  }

  const geom = obj.shape?.features?.[0]?.geometry;
  const raw = geom?.coordinates;

  if (
    Array.isArray(raw) &&
    typeof raw[0] === 'number' &&
    typeof raw[1] === 'number'
  ) {
    return { latitude: raw[1] as number, longitude: raw[0] as number };
  }

  if (Array.isArray(raw) && Array.isArray(raw[0])) {
    const ring = raw[0] as unknown as number[][];
    const first = ring?.[0];
    if (
      Array.isArray(first) &&
      typeof first[0] === 'number' &&
      typeof first[1] === 'number'
    ) {
      return { latitude: first[1], longitude: first[0] };
    }
  }

  return null;
};

// Minimum query length for the global map search to fire. The search
// is only ever kicked off by an explicit user action (Enter, the
// trailing "Search" button, or re-running a recent) — it does not
// auto-fire on keystroke — so a single character is enough; we let
// the user search as soon as they've typed *something*.
export const GIS_SEARCH_MIN_LENGTH = 1;
export const HYDRANT_PROXIMITY_KEYWORDS = ['hydrant', 'fire hydrant'];
export const HYDRANT_BUFFER_METERS = 500;
export const IGNORED_SPECIAL_CHARACTERS = /[-+()\s]/g;

const toStr = (v: unknown): string => {
  if (v == null) return '';
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
};

/** Case-insensitive attribute lookup — ArcGIS field casing varies across services. */
const getAttr = (attrs: Record<string, unknown>, name: string): string => {
  const lower = name.toLowerCase();
  for (const k of Object.keys(attrs)) {
    if (k.toLowerCase() === lower) return toStr(attrs[k]);
  }
  return '';
};

/** Try multiple field name variants and return the first non-empty match. */
const getFirstAttr = (
  attrs: Record<string, unknown>,
  names: string[],
): string => {
  for (const name of names) {
    const val = getAttr(attrs, name);
    if (val) return val;
  }
  return '';
};

const GENERIC_NAME_FIELDS = [
  'facility',
  'name',
  'label',
  'title',
  'description',
  'fullname',
  'full_name',
  'displayname',
  'display_name',
  'sitename',
  'site_name',
  'placename',
  'place_name',
  'location',
  'address',
];

/**
 * Find the best human-readable name from a feature's attributes by checking
 * well-known field names first, then falling back to any field whose key
 * contains a name-like substring.
 */
const findGenericName = (attrs: Record<string, unknown>): string => {
  const explicit = getFirstAttr(attrs, GENERIC_NAME_FIELDS);
  if (explicit) return explicit;

  const namePatterns = ['name', 'label', 'title', 'facility', 'desc'];
  for (const key of Object.keys(attrs)) {
    if (key.startsWith('_')) continue;
    const lower = key.toLowerCase();
    if (namePatterns.some(p => lower.includes(p))) {
      const val = toStr(attrs[key]);
      if (val) return val;
    }
  }
  return '';
};

const PARCEL_OWNER_FIELDS = [
  'Owner',
  'ownname',
  'owner_name',
  'ownername',
  'property_owner',
];
const PARCEL_OWNER2_FIELDS = ['Owner2', 'ownname2', 'coowner', 'owner_name2'];
const PARCEL_ID_FIELDS = [
  'parno',
  'Name',
  'APN',
  'parcel_id',
  'parcelid',
  'parcel_no',
  'PIN',
  'PID',
];

export const getSublayerKind = (title: string | undefined): string | null => {
  const lower = (title || '').toLowerCase();
  if (lower.includes('hydrant')) return 'hydrant';
  if (lower.includes('parcel')) return 'parcel';
  return null;
};

export const isProximitySearch = (text: string): boolean => {
  const lower = (text || '').toLowerCase().trim();
  return HYDRANT_PROXIMITY_KEYWORDS.some(kw => lower.includes(kw));
};

export const haversineDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number => {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const formatDistance = (meters: number): string => {
  const ft = meters * 3.28084;
  if (ft < 500) return `${Math.round(ft)} ft`;
  const mi = ft / 5280;
  if (mi < 10) return `${mi.toFixed(1)} mi`;
  return `${Math.round(mi)} mi`;
};

/**
 * Compare place rows by street address. Punctuation and casing differences
 * ("1 Main St." vs "1 main st") still count as the same address.
 */
export const normalizePlaceAddress = (
  address: string | undefined | null,
): string =>
  (address || '')
    .trim()
    .toLowerCase()
    .replace(/[.,#]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const DIRECTION_TOKENS: Record<string, string> = {
  n: 'n',
  north: 'n',
  s: 's',
  south: 's',
  e: 'e',
  east: 'e',
  w: 'w',
  west: 'w',
  ne: 'ne',
  northeast: 'ne',
  nw: 'nw',
  northwest: 'nw',
  se: 'se',
  southeast: 'se',
  sw: 'sw',
  southwest: 'sw',
};

const STREET_TYPES = new Set([
  'st',
  'street',
  'ave',
  'avenue',
  'blvd',
  'boulevard',
  'rd',
  'road',
  'dr',
  'drive',
  'ln',
  'lane',
  'ct',
  'court',
  'pl',
  'place',
  'ter',
  'terrace',
  'pkwy',
  'parkway',
  'cir',
  'circle',
  'hwy',
  'highway',
  'way',
  'trl',
  'trail',
]);

const STATE_ABBREVIATIONS: Record<string, string> = {
  alabama: 'al',
  alaska: 'ak',
  arizona: 'az',
  arkansas: 'ar',
  california: 'ca',
  colorado: 'co',
  connecticut: 'ct',
  delaware: 'de',
  'district of columbia': 'dc',
  florida: 'fl',
  georgia: 'ga',
  hawaii: 'hi',
  idaho: 'id',
  illinois: 'il',
  indiana: 'in',
  iowa: 'ia',
  kansas: 'ks',
  kentucky: 'ky',
  louisiana: 'la',
  maine: 'me',
  maryland: 'md',
  massachusetts: 'ma',
  michigan: 'mi',
  minnesota: 'mn',
  mississippi: 'ms',
  missouri: 'mo',
  montana: 'mt',
  nebraska: 'ne',
  nevada: 'nv',
  'new hampshire': 'nh',
  'new jersey': 'nj',
  'new mexico': 'nm',
  'new york': 'ny',
  'north carolina': 'nc',
  'north dakota': 'nd',
  ohio: 'oh',
  oklahoma: 'ok',
  oregon: 'or',
  pennsylvania: 'pa',
  'rhode island': 'ri',
  'south carolina': 'sc',
  'south dakota': 'sd',
  tennessee: 'tn',
  texas: 'tx',
  utah: 'ut',
  vermont: 'vt',
  virginia: 'va',
  washington: 'wa',
  'west virginia': 'wv',
  wisconsin: 'wi',
  wyoming: 'wy',
};

interface PlaceIdentity {
  number: string;
  street: string;
  direction: string;
  city: string;
  state: string;
}

const attrString = (attrs: Record<string, unknown>, key: string): string => {
  const value = attrs[key];
  return typeof value === 'string' ? value.trim() : '';
};

const normalizeDirection = (value: string): string =>
  DIRECTION_TOKENS[value.trim().toLowerCase().replace(/\./g, '')] || '';

const normalizeState = (value: string): string => {
  const cleaned = value.trim().toLowerCase().replace(/\./g, '');
  if (!cleaned || cleaned === 'usa' || cleaned === 'united states') return '';
  return STATE_ABBREVIATIONS[cleaned] || cleaned;
};

const parseStreetPart = (
  line: string,
): { number: string; direction: string; street: string } => {
  const tokens = line.replace(/[.#]/g, ' ').split(/\s+/).filter(Boolean);
  let number = '';
  const houseNumber = tokens[0];
  if (houseNumber && /^\d+[a-z]?$/i.test(houseNumber)) {
    tokens.shift();
    number = houseNumber.toLowerCase();
  }
  let direction = '';
  const leadingDirection = tokens[0];
  if (leadingDirection && normalizeDirection(leadingDirection)) {
    tokens.shift();
    direction = normalizeDirection(leadingDirection);
  }
  const trailingDirection = tokens[tokens.length - 1];
  if (trailingDirection && normalizeDirection(trailingDirection)) {
    tokens.pop();
    const trailing = normalizeDirection(trailingDirection);
    if (!direction) direction = trailing;
  }
  if (
    tokens.length &&
    STREET_TYPES.has(tokens[tokens.length - 1].toLowerCase())
  ) {
    tokens.pop();
  }
  return { number, direction, street: tokens.join(' ').toLowerCase() };
};

const parseLocality = (parts: string[]): { city: string; state: string } => {
  const locality = parts
    .map(part =>
      part
        .trim()
        .replace(/^(?:apt|apartment|unit|ste|suite|#)\s+\S+\s*/i, '')
        .trim(),
    )
    .filter(part => part && !/^\d{5}(?:-\d{4})?$/i.test(part))
    .filter(part => normalizeState(part) !== '');
  const city = (locality[0] || '').toLowerCase();
  const state = normalizeState(locality[1] || '');
  const cityIsState = !locality[1] && STATE_ABBREVIATIONS[city] !== undefined;
  return {
    city: cityIsState ? '' : city,
    state: cityIsState ? STATE_ABBREVIATIONS[city] : state,
  };
};

/**
 * Street + building number identify a place. City, state, and direction
 * (N/S/E/W/NE/NW/SE/SW) stay in the identity so the same number can show
 * in more than one of those. A different building number on that street
 * is not a separate result.
 */
export const identifyPlace = (candidate: PlaceCandidate): PlaceIdentity => {
  const attrs = (candidate.attributes || {}) as Record<string, unknown>;
  const address =
    candidate.address ||
    attrString(attrs, 'LongLabel') ||
    attrString(attrs, 'Match_addr');
  const [streetLine = '', ...rest] = address.split(',');
  const parsed = parseStreetPart(streetLine);
  const locality = parseLocality(rest);

  return {
    number: (attrString(attrs, 'AddNum') || parsed.number).toLowerCase(),
    street: (attrString(attrs, 'StName') || parsed.street).toLowerCase(),
    direction: normalizeDirection(
      attrString(attrs, 'StPreDir') ||
        attrString(attrs, 'StDir') ||
        parsed.direction,
    ),
    city: (attrString(attrs, 'City') || locality.city).toLowerCase(),
    state: normalizeState(
      attrString(attrs, 'RegionAbbr') ||
        attrString(attrs, 'Region') ||
        locality.state,
    ),
  };
};

/**
 * Remembers places already shown. The same street and building number is
 * kept again only when the city, state, or direction differs.
 */
export class PlaceResultDedupe {
  private exact = new Set<string>();
  private numberByStreet = new Map<string, string>();

  accept(candidate: PlaceCandidate): boolean {
    const id = identifyPlace(candidate);
    if (id.number && id.street) {
      const allowed = this.numberByStreet.get(id.street);
      if (allowed && allowed !== id.number) return false;
      if (!allowed) this.numberByStreet.set(id.street, id.number);
      const key = [id.number, id.street, id.direction, id.city, id.state].join(
        '|',
      );
      if (this.exact.has(key)) return false;
      this.exact.add(key);
      return true;
    }

    const fallback = `addr:${normalizePlaceAddress(candidate.address)}`;
    if (fallback === 'addr:' || this.exact.has(fallback)) return false;
    this.exact.add(fallback);
    return true;
  }
}

/**
 * If coordinates are in web mercator (abs > 180 / 90), project them to WGS84.
 * Otherwise return as-is.
 */
export const normalizePlaceCoords = (
  candidate: PlaceCandidate,
): PlaceCandidate => {
  if (!candidate.location) return candidate;
  const { x, y } = candidate.location;
  if (Math.abs(x) <= 180 && Math.abs(y) <= 90) return candidate;
  const [lng, lat] = webMercatorUtils.xyToLngLat(x, y);
  return { ...candidate, location: { x: lng, y: lat } };
};

export const getFeatureLabel = (
  attrs: Record<string, unknown>,
  kind: string,
): string => {
  if (kind === 'hydrant') {
    const street = getAttr(attrs, 'StreetName');
    return street ? `Hydrant — ${street}` : 'Fire Hydrant';
  }
  if (kind === 'parcel') {
    return (
      getFirstAttr(attrs, PARCEL_OWNER_FIELDS) ||
      getFirstAttr(attrs, PARCEL_ID_FIELDS) ||
      'Parcel'
    );
  }
  return findGenericName(attrs) || 'GIS Feature';
};

const formatFieldName = (raw: string): string =>
  raw
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();

export const getFeatureSublabel = (
  attrs: Record<string, unknown>,
  kind: string,
  geometry: FeatureGeometry | undefined,
  mapCenter: { latitude?: number; longitude?: number },
): string => {
  const parts: string[] = [];

  const matchedField = toStr(attrs._matchedField);
  const matchedValue = toStr(attrs._matchedValue);
  if (matchedField && matchedValue) {
    parts.push(`${formatFieldName(matchedField)}: ${matchedValue}`);
  }

  if (kind === 'hydrant') {
    const status = (attrs.Status as string) || (attrs.STATUS as string) || '';
    if (status) parts.push(`Status: ${status}`);

    const lat = geometry?.centroid?.latitude;
    const lng = geometry?.centroid?.longitude;
    if (lat != null && lng != null) {
      parts.push(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
      if (mapCenter?.latitude != null && mapCenter?.longitude != null) {
        parts.push(
          formatDistance(
            haversineDistance(
              mapCenter.latitude,
              mapCenter.longitude,
              lat,
              lng,
            ),
          ),
        );
      }
    }
    return parts.join('  ·  ');
  }
  if (kind === 'parcel') {
    const owner = getFirstAttr(attrs, PARCEL_OWNER_FIELDS);
    const owner2 = getFirstAttr(attrs, PARCEL_OWNER2_FIELDS);
    const parcelId = getFirstAttr(attrs, PARCEL_ID_FIELDS);
    if (owner && !parts.some(p => p.includes(owner)))
      parts.push(`Owner: ${owner}`);
    if (owner2) parts.push(`Owner 2: ${owner2}`);
    if (parcelId && !parts.some(p => p.includes(parcelId)))
      parts.push(`Parcel #: ${parcelId}`);
    return parts.join('  ·  ');
  }

  const sourceLayer = toStr(attrs._sourceLayer);
  if (sourceLayer) parts.push(sourceLayer);
  const addr =
    getAttr(attrs, 'address') ||
    getAttr(attrs, 'location') ||
    getAttr(attrs, 'addr');
  if (addr && !parts.some(p => p.includes(addr))) parts.push(addr);
  if (
    geometry?.centroid &&
    mapCenter?.latitude != null &&
    mapCenter?.longitude != null
  ) {
    parts.push(
      formatDistance(
        haversineDistance(
          mapCenter.latitude,
          mapCenter.longitude,
          geometry.centroid.latitude,
          geometry.centroid.longitude,
        ),
      ),
    );
  }
  return parts.join('  ·  ');
};
